import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "danh-muc-toa-xe-search-autocomplete-status"
}
const _hoisted_2 = {
  key: 1,
  class: "danh-muc-toa-xe-search-item"
}
const _hoisted_3 = {
  key: 0,
  class: "danh-muc-toa-xe-search-item__helptext"
}

import { ref, watch, nextTick } from 'vue';
import type { ElAutocomplete } from 'element-plus';
import { swalErrNotification } from '@/core/helpers/utils';
import DanhMucToaXeService from '@/core/services/DanhMucToaXe.service';
import { IDanhMucToaXeResponseBase } from '@/core/interfaces/ApiResponses';

type SuggestionItem = IDanhMucToaXeResponseBase & {
  __loading?: boolean;
  __empty?: boolean;
};

type AutocompleteInstance = InstanceType<typeof ElAutocomplete> & {
  activated: boolean;
  suggestionDisabled: boolean;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'DanhMucToaXeSearchInput',
  props: {
  fullWidth: {
    type: Boolean,
    default: false,
  },
  id: {
    type: Number,
    default: null,
  },
  displayLabel: {
    type: String,
    default: '',
  },
  propName: {
    type: String,
    default: 'id_dm_toa_xe',
  },
  valueKey: {
    type: String,
    default: 'ten_danh_muc',
  },
  tabindex: {
    type: [Number, String],
    default: 0,
  },
},
  emits: ["clear", "select"],
  setup(__props, { expose: __expose, emit: __emit }) {

const LOADING_SUGGESTION: SuggestionItem = {
  __loading: true,
  id: -1,
  ten_danh_muc: 'Đang tìm kiếm...',
  ma_danh_muc: '',
};

const EMPTY_SUGGESTION: SuggestionItem = {
  __empty: true,
  id: -2,
  ten_danh_muc: 'Không có kết quả phù hợp!',
  ma_danh_muc: '',
};

const props = __props;

const formatSelectedDisplay = (ten: string, ma?: string) => {
  if (!ten) {
    return '';
  }

  if (!ma) {
    return ten;
  }

  return `${ten} / (${ma})`;
};

const parseDisplayLabel = (label: string) => {
  const slashMatch = label.match(/^(.*?)\s*\/\s*\((.+)\)\s*$/);

  if (slashMatch) {
    return {
      ten: slashMatch[1].trim(),
      ma: slashMatch[2].trim(),
    };
  }

  const dashMatch = label.match(/^(.*?)\s*-\s*\((.+)\)\s*$/);

  if (dashMatch) {
    return {
      ten: dashMatch[1].trim(),
      ma: dashMatch[2].trim(),
    };
  }

  return {
    ten: label.trim(),
    ma: '',
  };
};

const tenDanhMuc = ref<string>(props.displayLabel);
const selectedId = ref<number | null>(props.id);
const selectedTenDanhMuc = ref('');
const selectedMaDanhMuc = ref('');
const autocompleteRef = ref<AutocompleteInstance>();

const applySelectedDisplay = () => {
  if (!selectedId.value || !selectedTenDanhMuc.value) {
    return;
  }

  tenDanhMuc.value = formatSelectedDisplay(
    selectedTenDanhMuc.value,
    selectedMaDanhMuc.value
  );
};

const setSelectedItem = (ten: string, ma = '') => {
  selectedTenDanhMuc.value = ten;
  selectedMaDanhMuc.value = ma;
  tenDanhMuc.value = formatSelectedDisplay(ten, ma);
};

watch(
  () => props.displayLabel,
  (label) => {
    if (label) {
      const { ten, ma } = parseDisplayLabel(label);
      selectedTenDanhMuc.value = ten;
      selectedMaDanhMuc.value = ma;
      tenDanhMuc.value = formatSelectedDisplay(ten, ma);

      return;
    }

    if (!props.id) {
      tenDanhMuc.value = '';
    }
  },
  { immediate: true },
);

watch(() => props.id, async (newId, previousId) => {
  if (!newId) {
    if (!props.displayLabel) {
      tenDanhMuc.value = '';
    }

    selectedId.value = null;
    selectedTenDanhMuc.value = '';
    selectedMaDanhMuc.value = '';

    return;
  }

  selectedId.value = newId;

  if (props.displayLabel || (previousId && previousId === newId)) {
    return;
  }

  try {
    const {
      data: {
        data: {
          ten_danh_muc,
          ma_danh_muc,
        }
      }
    } = await DanhMucToaXeService.get(newId);

    setSelectedItem(ten_danh_muc, ma_danh_muc || '');
  } catch (error) {
    await swalErrNotification(error, 'Có lỗi xảy ra');
  }
});

const emits = __emit;

const isStatusSuggestion = (item: SuggestionItem) => item.__loading || item.__empty;

const handleOptionSelected = (item: SuggestionItem) => {
  if (isStatusSuggestion(item)) {
    return;
  }

  selectedId.value = item.id;
  setSelectedItem(item.ten_danh_muc, item.ma_danh_muc || '');
  emits('select', {
    id: item.id,
    ten_danh_muc: item.ten_danh_muc,
    ma_danh_muc: item.ma_danh_muc,
  });

  // Release focus so parent can advance to the next tabindex field (Enter/click)
  nextTick(() => {
    autocompleteRef.value?.blur?.();
  });
};

const reopenSuggestionsAfterClear = () => {
  nextTick(() => {
    const autocomplete = autocompleteRef.value;

    if (!autocomplete) {
      return;
    }

    // ElAutocomplete sets activated=false on clear; focus() alone does not
    // re-open the dropdown when the input already has focus.
    autocomplete.suggestionDisabled = false;
    autocomplete.activated = true;
    autocomplete.getData('');
    autocomplete.focus();
  });
};

const handleClear = () => {
  selectedId.value = null;
  selectedTenDanhMuc.value = '';
  selectedMaDanhMuc.value = '';
  tenDanhMuc.value = '';
  emits('clear');
  reopenSuggestionsAfterClear();
};

const onFocus = (event: FocusEvent) => {
  // Search with ten_danh_muc only, not the full "ten / (ma)" display value
  if (selectedId.value && selectedTenDanhMuc.value) {
    tenDanhMuc.value = selectedTenDanhMuc.value;
  }

  const target = event.target as HTMLInputElement | null;
  target?.select?.();

  nextTick(() => {
    const autocomplete = autocompleteRef.value;

    if (!autocomplete || !selectedTenDanhMuc.value) {
      return;
    }

    autocomplete.suggestionDisabled = false;
    autocomplete.activated = true;
    autocomplete.getData(selectedTenDanhMuc.value);
  });
};

const onBlur = () => {
  // Delay restore so parent can move focus to the next field after Enter/click
  setTimeout(() => {
    if (selectedId.value && selectedTenDanhMuc.value) {
      applySelectedDisplay();
    }
  }, 80);
};

const focus = () => {
  nextTick(() => {
    autocompleteRef.value?.focus();
  });
};

const blur = () => {
  autocompleteRef.value?.blur?.();
};

__expose({ focus, blur });

const resolveSearchTerm = (queryString: string) => {
  if (
    selectedId.value &&
    selectedTenDanhMuc.value &&
    queryString ===
      formatSelectedDisplay(selectedTenDanhMuc.value, selectedMaDanhMuc.value)
  ) {
    return selectedTenDanhMuc.value;
  }

  return queryString;
};

const query = async (
  queryString: string,
  cb: (results: SuggestionItem[]) => void,
) => {
  cb([LOADING_SUGGESTION]);

  try {
    const {
      data: {
        data: {
          data
        }
      }
    } = await DanhMucToaXeService.list(1, 1000, resolveSearchTerm(queryString));

    if (!data?.length) {
      cb([EMPTY_SUGGESTION]);

      return;
    }

    cb(data);
  } catch (error) {
    await swalErrNotification(error, 'Có lỗi xảy ra');
    cb([EMPTY_SUGGESTION]);
  }
};

return (_ctx: any,_cache: any) => {
  const _component_el_autocomplete = _resolveComponent("el-autocomplete")!
  const _component_el_form_item = _resolveComponent("el-form-item")!

  return (_openBlock(), _createBlock(_component_el_form_item, {
    class: _normalizeClass(["mb-0", { 'w-full': __props.fullWidth }]),
    prop: __props.propName
  }, {
    default: _withCtx(() => [
      _createVNode(_component_el_autocomplete, {
        ref_key: "autocompleteRef",
        ref: autocompleteRef,
        modelValue: tenDanhMuc.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tenDanhMuc).value = $event)),
        class: _normalizeClass({ 'w-full': __props.fullWidth }),
        clearable: "",
        "hide-loading": "",
        "fetch-suggestions": query,
        "highlight-first-item": "",
        name: __props.propName,
        placeholder: "Nhập để tìm kiếm ...",
        "popper-class": "danh-muc-toa-xe-search-autocomplete-popper",
        tabindex: __props.tabindex,
        "trigger-on-focus": true,
        onClear: handleClear,
        onFocus: onFocus,
        onBlur: onBlur,
        onSelect: handleOptionSelected
      }, {
        default: _withCtx(({ item }) => [
          (item.__loading || item.__empty)
            ? (_openBlock(), _createElementBlock("span", _hoisted_1, _toDisplayString(item.ten_danh_muc), 1))
            : (_openBlock(), _createElementBlock("span", _hoisted_2, [
                _createElementVNode("span", null, _toDisplayString(item[__props.valueKey]), 1),
                (item.ma_danh_muc)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_3, " / (" + _toDisplayString(item.ma_danh_muc) + ") ", 1))
                  : _createCommentVNode("", true)
              ]))
        ]),
        _: 1
      }, 8, ["modelValue", "class", "name", "tabindex"])
    ]),
    _: 1
  }, 8, ["class", "prop"]))
}
}

})